import React, { useCallback, useEffect, useState } from 'react'
import { Form, Button, Tooltip } from 'antd'
import styled from 'styled-components'
import {
  StepIndicator,
  TaskBasicInfo,
  ValidationModeSelector,
  AgentToContainerConfig,
  ContainerAgentToListenerConfig,
  SecurityDeviceSelector,
  ExecutionTypeSelector,
  ExecutionTimeSelector,
  SchedulerConfig,
  ExecutionPriorityInput,
  TemplateIncrementalToggle,
  AutoReportConfig,
} from './step1/components'
import { TemplateSelector, UseCaseSelector } from './step2/components'
import {
  useAgentList,
  useHostList,
  useTaskGroup,
  useDeviceListByHostId,
  findAgentById,
  useEmailList,
  processFormData,
  useCreateTask,
} from './utils'
import { Message } from '@/components'
import { useNavigate } from 'react-router'
import moment from 'moment'

// 转换Agent数据格式
const transformAgentData = (items: any[]) =>
  items.map(domain => ({
    value: domain.id,
    label: domain.name,
    children: domain.os
      .filter((osType: any) => osType.osSystem !== 'OsWin') // 过滤掉Windows，只保留Linux
      .map((osType: any) => ({
        value: osType.osSystem,
        label: 'Linux', // 只显示Linux
        children: osType.agents.map((agent: any) => ({
          value: agent.agentId,
          hostName: agent.agentName, // 保存hostName用于后续查找
          hostId: agent.hostId, // 保存hostId用于获取设备列表
          label: (
            <div className="agent-item">
              <div className="agent-item-name">
                <Tooltip title={agent.ip.join(',')} overlayStyle={{ maxWidth: '400px' }}>
                  {agent.agentName}
                  <span style={{ color: '#999', marginLeft: 4 }}>({agent.ip[0]})</span>
                </Tooltip>
              </div>
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  background: agent.aliveStatus === 'AliveStatusTrue' ? '#67C23A' : '#677280',
                  borderRadius: '50%',
                }}
              />
            </div>
          ),
          selectedLabel: (
            <div>
              <Tooltip
                title={agent.ip.join(',')}
                overlayStyle={{ maxWidth: '400px' }}
                getPopupContainer={() => document.body}
              >
                {agent.agentName}
                <span style={{ color: '#999', marginLeft: 4 }}>({agent.ip[0]})</span>
              </Tooltip>
            </div>
          ),
          disabled: agent.aliveStatus === 'AliveStatusFalse',
        })),
      })),
  }))

// 转换Host数据格式
const transformHostData = (items: any[]) =>
  items.map(domain => ({
    value: domain.id,
    label: domain.name,
    children: domain.os
      .filter((osType: any) => osType.osSystem !== 'OsWin') // 过滤掉Windows，只保留Linux
      .map((osType: any) => ({
        value: osType.osSystem,
        label: 'Linux', // 只显示Linux
        children: osType.hosts.map((host: any) => ({
          value: host.hostId,
          hostName: host.hostName, // 保存hostName用于后续查找
          label: (
            <div className="agent-item">
              <div className="agent-item-name">{host.hostName}</div>
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  background: host.aliveStatus === 'AliveStatusTrue' ? '#67C23A' : '#677280',
                  borderRadius: '50%',
                }}
              />
            </div>
          ),
          selectedLabel: host.hostName,
          disabled: host.aliveStatus === 'AliveStatusFalse',
          children: host.ip.map((ip: any) => ({
            value: ip,
            label: ip,
          })),
        })),
      })),
  }))

// 转换设备数据格式
const transformDeviceData = (deviceList: any[]) =>
  deviceList.map(domain => ({
    value: domain.deviceType,
    label: domain.deviceType,
    children: domain.devices.map((device: any) => ({
      value: device.deviceId,
      label: (
        <div className="agent-item">
          <div className="agent-item-name">
            {device.deviceName} ｜
            <span style={{ color: '#999', marginLeft: 4 }}>({device.deviceIp})</span>
          </div>
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              background:
                device.deviceStatus === 'DatasourceClientStatusOnline' ? '#67C23A' : '#677280',
              borderRadius: '50%',
            }}
          />
        </div>
      ),
      selectedLabel: (
        <div>
          {device.deviceName}｜
          <span style={{ color: '#999', marginLeft: 4 }}>({device.deviceIp})</span>
        </div>
      ),
      disabled: device.deviceStatus === 'DatasourceClientStatusOffline',
    })),
  }))

const CreateTask: React.FC = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [form] = Form.useForm()

  const [dataOptions, setDataOptions] = useState<any>({})
  const [taskParams, setTaskParams] = useState<any>({})
  const [prevValidationModes, setPrevValidationModes] = useState<string[]>(['agentToContainer'])

  // 判断是否应该禁用创建任务和保存为模板按钮
  const isCreateTaskDisabled = !taskParams.resourceIds || taskParams.resourceIds.length === 0

  // 获取任务分组
  const [getTaskGroup] = useTaskGroup()
  // 获取agent列表
  const [getAgentList] = useAgentList()
  // 获取主机列表
  const [getHostList] = useHostList()
  // 根据hostId获取设备列表
  const [getDeviceListByHostId] = useDeviceListByHostId()
  // 获取人员邮箱列表
  const [getEmailList] = useEmailList()
  // 创建任务
  const [createTask, createTaskLoading] = useCreateTask()

  const handleNext = async () => {
    try {
      await form.validateFields()
      form.submit()
      setCurrentStep(2)
    } catch (error) {
      console.log('表单验证失败:', error)
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
    // 清空所有选中的模板和用例数据
    setTaskParams(prev => ({
      ...prev,
      selectedTemplates: [],
      executeUpdateTemplates: [],
      resourceIds: [],
      templateUseCaseCount: {},
    }))
    console.log('清空模板和用例选择，保留第一步表单信息')
  }

  const onFinish = (values: any) => {
    const params = processFormData(values, dataOptions)

    // 检测关键参数是否变化，如果变化则清空模板选择
    const currentSnapshot = {
      os: params.os,
      verificationModeIds: params.verificationModeIds,
    }

    if (params.periodType) {
      params.schedulerType = params.periodType
      params.cronSchedule = {
        dayOfWeek: values.cronSchedule.dayOfWeek,
        dayOfMonth: values.cronSchedule.dayOfMonth,
        timeOfDay: values.cronSchedule.timeOfDay
          ? moment(values.cronSchedule.timeOfDay).format('HH:mm:ss')
          : undefined,
      }
      delete params.periodType
    }

    if (params.reportConfig.filter) {
      params.reportConfig.condition = params.reportConfig.condition.split(',')
    }

    const shouldClearTemplateSelection =
      taskParams._templateParamsSnapshot &&
      JSON.stringify(currentSnapshot) !== JSON.stringify(taskParams._templateParamsSnapshot)

    setTaskParams(prev => ({
      ...params,
      // 如果参数变化，清空模板选择；否则保持原有选择
      selectedTemplates: shouldClearTemplateSelection ? [] : prev.selectedTemplates || [],
      executeUpdateTemplates: shouldClearTemplateSelection ? [] : prev.executeUpdateTemplates || [],
      // 如果参数变化，清空用例统计；否则保持原有统计
      templateUseCaseCount: shouldClearTemplateSelection ? {} : prev.templateUseCaseCount || {},
      // 保存参数快照
      _templateParamsSnapshot: currentSnapshot,
    }))
  }

  const handleCreateTask = async () => {
    // 检查是否有选中的用例
    if (!taskParams.resourceIds || taskParams.resourceIds.length === 0) {
      Message.warning('请至少选择一个用例')
      return
    }

    // 转换模板数据格式
    const templateList =
      taskParams.selectedTemplates?.map((id: string) => ({
        id,
        useLatestResource: taskParams.executeUpdateTemplates?.includes(id) || false,
      })) || []

    // 构建最终的请求参数
    const finalTaskParams = {
      ...taskParams,
      templateList,
      // 移除原始字段，避免重复
      os: undefined,
      isLatest: undefined,
      verificationModeIds: undefined,
      selectedTemplates: undefined,
      executeUpdateTemplates: undefined,
      templateUseCaseCount: undefined,
      _templateParamsSnapshot: undefined,
    }

    try {
      const res = await createTask(finalTaskParams)

      if (!res || !res.data) return
      Message.success('创建任务成功')
      navigate('/validation/cloud/containersecurity?tab=task')
    } catch (error) {}
  }

  // 处理模板选择状态变化
  const handleTemplateSelectionChange = useCallback(
    (selectedTemplates: string[], executeUpdateTemplates: string[]) => {
      setTaskParams(prev => ({
        ...prev,
        selectedTemplates,
        executeUpdateTemplates,
      }))
    },
    [],
  )

  // 处理用例选择对模板列表的影响
  const handleUseCaseSelectionChange = useCallback(
    (
      selectedUseCases: string[],
      useCaseTemplateMap: Record<string, string[]>, // 用例ID -> 模板ID数组的映射
      isSelectAll: boolean = false, // 是否为全选所有
    ) => {
      let templateUseCaseCount: Record<string, number | string> = {}

      if (isSelectAll) {
        // 全选所有：使用特殊标识
        templateUseCaseCount = { isAll: 'isAll' }
      } else {
        // 部分选择：统计每个模板被选中的用例数量
        selectedUseCases.forEach(useCaseId => {
          const templateIds = useCaseTemplateMap[useCaseId] || []
          templateIds.forEach(templateId => {
            templateUseCaseCount[templateId] =
              ((templateUseCaseCount[templateId] as number) || 0) + 1
          })
        })
      }

      // 更新taskParams中的模板用例统计和选中的用例ID
      setTaskParams(prev => ({
        ...prev,
        templateUseCaseCount, // 保存每个模板的用例数量统计
        resourceIds: selectedUseCases, // 保存选中的用例ID
      }))
    },
    [],
  )

  // 处理验证模式变化，清空被取消模式下的表单信息
  const handleValidationModeChange = useCallback(
    (checkedValues: string[], prevValues: string[]) => {
      const removedModes = prevValues.filter(mode => !checkedValues.includes(mode))
      console.log('removedModes', removedModes)
      // 清空被取消模式下的表单信息
      removedModes.forEach(mode => {
        if (mode === 'agentToContainer') {
          form.setFieldsValue({
            verificationMode: {
              agentToContainer: undefined,
            },
          })
        } else if (mode === 'containerAgentToListener') {
          form.setFieldsValue({
            verificationMode: {
              containerToListener: undefined,
            },
          })
        }
      })

      form.setFieldsValue({
        deviceIds: undefined,
      })

      // 更新前一个状态
      setPrevValidationModes(checkedValues)
    },
    [form],
  )

  // 根据agentId获取对应的hostId并请求设备列表
  const fetchDeviceListByAgentId = useCallback(
    async (agentId: string) => {
      if (!agentId) return

      try {
        // 从agentOptions中查找对应的hostId
        const agentInfo = findAgentById(agentId, dataOptions.agentOptions || [])
        if (agentInfo && agentInfo.hostId) {
          const deviceRes = await getDeviceListByHostId(agentInfo.hostId)

          // 更新设备选项
          setDataOptions(prev => ({
            ...prev,
            deviceOptions: transformDeviceData(deviceRes.data.deviceList),
          }))
        }
      } catch (error) {
        console.error('获取设备列表失败:', error)
      }
    },
    [dataOptions.agentOptions, getDeviceListByHostId],
  )

  // 抽象数据请求方法
  const fetchInitialData = useCallback(async () => {
    try {
      const [taskGroupRes, agentRes, hostRes, emailRes] = await Promise.all([
        getTaskGroup({ page: 1, limit: 9999 }),
        getAgentList(),
        getHostList(),
        getEmailList(),
      ])

      form.setFieldsValue({
        groupId: taskGroupRes.data.groups.find(item => item.groupName === '常规任务')?.groupId,
      })

      setDataOptions({
        taskGroup: taskGroupRes.data.groups.map(item => ({
          label: item.groupName,
          value: item.groupId,
        })),
        agentOptions: transformAgentData(agentRes.data.items),
        listenerOptions: transformHostData(hostRes.data.items),
        deviceOptions: [], // 初始化为空数组
        emailOptions: emailRes.data.emailUsers.map(item => ({
          label: `${item.name}(${item.email})`,
          email: item.email,
          name: item.name,
          value: item.id,
        })),
      })
    } catch (error) {
      Message.error('数据加载失败')
    } finally {
    }
  }, [])

  useEffect(() => {
    if (currentStep === 1) {
      fetchInitialData()
    }
  }, [fetchInitialData])

  return (
    <Wrapper>
      <div className="container">
        <StepIndicator currentStep={currentStep} />

        <FormWrapper>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              schedulerType: 'scheduler_type_immediate',
              periodType: 'scheduler_type_daily',
              cronSchedule: {
                dayOfWeek: [],
                dayOfMonth: [],
                timeOfDay: null,
              },
              reduceSpeed: 20,
              priority: 3,
            }}
            colon={false}
          >
            {currentStep === 1 && (
              <FormContent>
                <TaskBasicInfo dataOptions={dataOptions} />

                <ValidationModeSelector
                  onModeChange={handleValidationModeChange}
                  prevValues={prevValidationModes}
                />

                <Form.Item dependencies={['validationMode']} className="validation-mode-form-item">
                  {({ getFieldValue }) => {
                    const validationMode = getFieldValue('validationMode') || []
                    return (
                      <>
                        {validationMode.includes('agentToContainer') && (
                          <AgentToContainerConfig
                            dataOptions={dataOptions}
                            onAgentChange={fetchDeviceListByAgentId}
                          />
                        )}
                        {validationMode.includes('containerAgentToListener') && (
                          <ContainerAgentToListenerConfig
                            dataOptions={dataOptions}
                            onAgentChange={fetchDeviceListByAgentId}
                          />
                        )}
                      </>
                    )
                  }}
                </Form.Item>

                <SecurityDeviceSelector dataOptions={dataOptions} />

                <ExecutionTypeSelector />

                <Form.Item dependencies={['schedulerType']} style={{ marginBottom: 0 }}>
                  {({ getFieldValue }) => {
                    const schedulerType = getFieldValue('schedulerType')
                    if (schedulerType === 'scheduler_type_reserve') {
                      return <ExecutionTimeSelector />
                    }
                    if (schedulerType === 'periodic') {
                      return <SchedulerConfig />
                    }
                    return null
                  }}
                </Form.Item>

                <ExecutionPriorityInput />
                <Form.Item dependencies={['schedulerType']} style={{ marginBottom: 0 }}>
                  {({ getFieldValue }) => {
                    const schedulerType = getFieldValue('schedulerType')
                    if (schedulerType === 'periodic') {
                      return <TemplateIncrementalToggle />
                    }
                    return null
                  }}
                </Form.Item>

                <AutoReportConfig dataOptions={dataOptions} />
              </FormContent>
            )}

            {currentStep === 2 && (
              <Step2Container>
                <TemplateSelector
                  taskParams={taskParams}
                  onTemplateSelectionChange={handleTemplateSelectionChange}
                />
                <UseCaseSelector
                  taskParams={taskParams}
                  onUseCaseSelectionChange={handleUseCaseSelectionChange}
                  isCreateTaskDisabled={isCreateTaskDisabled}
                />
              </Step2Container>
            )}

            <FooterActions>
              {currentStep === 1 && (
                <Button type="primary" onClick={handleNext}>
                  下一步
                </Button>
              )}
              {currentStep === 2 && (
                <>
                  <Button type="default" onClick={handleBack} style={{ marginRight: 8 }}>
                    上一步
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleCreateTask}
                    loading={createTaskLoading}
                    disabled={isCreateTaskDisabled}
                  >
                    创建任务
                  </Button>
                </>
              )}
            </FooterActions>
          </Form>
        </FormWrapper>
      </div>
    </Wrapper>
  )
}

export default CreateTask

const Wrapper = styled.div`
  padding: 16px;

  .container {
    width: 100%;
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
`

const FormWrapper = styled.div`
  padding: 24px;
  padding-bottom: 12px;
  flex: 1;
  .ant-form-item-label > label {
    font-weight: 500;
  }
`

const FormContent = styled.div`
  margin-bottom: 24px;

  .validation-mode-form-item {
    margin-bottom: 0;
  }

  .agent-item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .agent-item-name {
      margin-right: 12px;
    }
  }
`

const Step2Container = styled.div`
  display: flex;
  justify-content: space-between;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
`
